import { useMemo, useState } from "react";
import { X, History } from "lucide-react";

export interface TargetHistoryRow {
  year: number;
  month: number;
  quarter: number;
  label: string;
  target: number;
  achieved: number;
  pct: number;
  bonus: { tierName: string; bonusType: string; bonusValue: number } | null;
  status: string;
}

const money = (n: number) =>
  `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const statusStyle = (s: string) => {
  if (s === "Target Achieved") return "bg-[#dcfce7] text-[#0f8a63] border-[#bbf7d0]";
  if (s === "In Progress") return "bg-primary-container text-on-primary-container border-primary";
  if (s === "No Target") return "bg-surface-container text-on-surface-variant border-outline-variant";
  return "bg-error-container text-error border-error-container";
};

const bonusLabel = (b: TargetHistoryRow["bonus"]) =>
  b ? `${b.tierName}: ${b.bonusType === "Percentage" ? `${b.bonusValue}%` : `₨${(b.bonusValue || 0).toLocaleString()}`}` : "—";

export function TargetHistoryModal({
  rows,
  onClose,
}: {
  rows: TargetHistoryRow[];
  onClose: () => void;
}) {
  const [month, setMonth] = useState<string>("all");
  const [quarter, setQuarter] = useState<string>("all");
  const [year, setYear] = useState<string>("all");

  const years = useMemo(
    () => Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => b - a),
    [rows]
  );

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const filtered = rows.filter(
    (r) =>
      (month === "all" || String(r.month) === month) &&
      (quarter === "all" || String(r.quarter) === quarter) &&
      (year === "all" || String(r.year) === year)
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-container-lowest w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant">
          <div>
            <h2 className="text-lg font-bold text-on-surface flex items-center gap-2">
              <History size={18} className="text-primary" /> Target History
            </h2>
            <p className="text-[11px] text-on-surface-variant mt-0.5">
              Monthly target vs revenue achieved, with bonus earned and status.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-outline hover:bg-surface-container rounded-full">
            <X size={18} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 px-6 py-3 border-b border-outline-variant">
          <label className="flex items-center gap-2 text-xs font-medium text-on-surface-variant">
            Month
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="px-2 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={String(i + 1)}>{m}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-on-surface-variant">
            Quarter
            <select
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              className="px-2 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All</option>
              <option value="1">Q1</option>
              <option value="2">Q2</option>
              <option value="3">Q3</option>
              <option value="4">Q4</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-on-surface-variant">
            Year
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="px-2 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All</option>
              {years.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="cre-scroll overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-outline">No target history for this filter.</div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-container-low text-[11px] font-bold uppercase tracking-wide text-on-surface-variant sticky top-0">
                <tr>
                  {["Month", "Qtr", "Target", "Achieved", "%", "Bonus", "Status"].map((h) => (
                    <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant">
                {filtered.map((r) => (
                  <tr key={`${r.year}-${r.month}`} className="hover:bg-surface-container-low/60">
                    <td className="px-3 py-2.5 font-medium text-on-surface whitespace-nowrap">{r.label}</td>
                    <td className="px-3 py-2.5 text-on-surface-variant">Q{r.quarter}</td>
                    <td className="px-3 py-2.5">{money(r.target)}</td>
                    <td className="px-3 py-2.5 font-semibold text-on-surface">{money(r.achieved)}</td>
                    <td className={`px-3 py-2.5 font-bold ${r.pct >= 100 ? "text-[#10b981]" : r.pct >= 60 ? "text-amber-600" : "text-error"}`}>
                      {r.pct}%
                    </td>
                    <td className="px-3 py-2.5 text-on-surface-variant whitespace-nowrap">{bonusLabel(r.bonus)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${statusStyle(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
